import PropTypes from 'prop-types';

/**
 * Input - Reusable input/textarea component
 * 
 * Supports: text, email, tel, textarea
 * Features: label, placeholder, error, required indicator, validation
 * 
 * Design System:
 * - Text size: text-lg (consistent)
 * - Focus: primary green ring
 * - Error: red border and message
 * - Accessible: proper label association
 */
const Input = ({
  label,
  type = 'text',
  name,
  value,
  onChange,
  placeholder = '',
  required = false,
  error = '',
  disabled = false,
  rows = 4,
  className = '',
}) => {
  // Generate unique ID for accessibility
  const inputId = `input-${name}`;

  // Base styles for input/textarea
  const baseStyles = 'w-full px-4 py-3 text-lg border rounded-lg transition-all duration-200 focus:outline-none focus:ring-2 focus:ring-primary-500 focus:border-transparent';
  
  // Error styles
  const errorStyles = error 
    ? 'border-red-500 focus:ring-red-500' 
    : 'border-gray-300 focus:ring-primary-500';
  
  // Disabled styles
  const disabledStyles = disabled 
    ? 'bg-gray-100 cursor-not-allowed opacity-60' 
    : 'bg-white';
  
  // Combine all styles
  const inputClasses = `${baseStyles} ${errorStyles} ${disabledStyles} ${className}`;

  return (
    <div className="mb-4">
      {/* Label */}
      {label && (
        <label 
          htmlFor={inputId} 
          className="block text-lg font-medium text-gray-700 mb-2"
        >
          {label}
          {required && <span className="text-red-500 ml-1">*</span>}
        </label>
      )}
      
      {/* Input or Textarea */}
      {type === 'textarea' ? (
        <textarea
          id={inputId}
          name={name}
          value={value}
          onChange={onChange}
          placeholder={placeholder}
          required={required}
          disabled={disabled}
          rows={rows}
          className={inputClasses}
        />
      ) : (
        <input
          id={inputId}
          type={type}
          name={name}
          value={value}
          onChange={onChange}
          placeholder={placeholder}
          required={required}
          disabled={disabled}
          className={inputClasses}
        />
      )}
      
      {/* Error Message */}
      {error && (
        <p className="mt-2 text-sm text-red-600">
          {error}
        </p>
      )}
    </div>
  );
};

Input.propTypes = {
  label: PropTypes.string,
  type: PropTypes.oneOf(['text', 'email', 'tel', 'textarea']),
  name: PropTypes.string.isRequired,
  value: PropTypes.string.isRequired,
  onChange: PropTypes.func.isRequired,
  placeholder: PropTypes.string,
  required: PropTypes.bool,
  error: PropTypes.string,
  disabled: PropTypes.bool,
  rows: PropTypes.number,
  className: PropTypes.string,
};

export default Input;

